/**
 * The viewport's floating toolbars (reference 02).
 * Left: projection (Perspective ▾ / Orthographic, or a camera to look
 * through and pilot, as in Unreal's viewport menu), view direction (Top,
 * Front, … or Free once rotated; independent of projection, as in Blender and
 * Unity), view mode (Lit ▾) and show flags.
 * Right: grid snap, rotation snap, the snap toggle and camera speed.
 * Controls dispatch `editor.setSetting` (review D-3); projection and view
 * dispatch `camera.setProjection` and `camera.setView`.
 */
import type { CommandRequest } from "../../commands/types";
import { useEditor } from "../EditorContext";
import { useEditorSettings, useStoreSelector, useWorldSnapshot } from "../hooks/editorHooks";
import { useStore } from "../hooks/useStore";
import type { SettingKey, SettingsValues } from "../settings/EditorSettings";
import { Dropdown } from "../ui/Dropdown";
import { CameraSpeedIcon, GridIcon, MagnetIcon, RotateIcon } from "../ui/icons";
import { axisViewOf } from "./cameraMath";
import type { AxisView, Projection } from "./cameraMath";
import { AXIS_VIEWS, PROJECTIONS } from "./ViewportCamera";

const PROJECTION_LABELS: Readonly<Record<Projection, string>> = {
  perspective: "Perspective",
  orthographic: "Orthographic",
};

const VIEW_LABELS: Readonly<Record<AxisView, string>> = {
  top: "Top",
  bottom: "Bottom",
  left: "Left",
  right: "Right",
  front: "Front",
  back: "Back",
};

const VIEW_MODES: readonly { value: SettingsValues["viewMode"]; label: string }[] = [
  { value: "lit", label: "Lit" },
  { value: "unlit", label: "Unlit" },
  { value: "wireframe", label: "Wireframe" },
];

const SHOW_FLAGS: readonly { key: SettingKey & `show${string}`; label: string }[] = [
  { key: "showGrid", label: "Grid" },
  { key: "showBounds", label: "Bounds" },
  { key: "showWireframe", label: "Wireframe" },
  { key: "showCameras", label: "Cameras" },
  { key: "showAllFrustums", label: "All camera frustums" },
  { key: "showStats", label: "Stats" },
];

/** Unreal-style presets; grid snap is in centimeters (A-08). */
const GRID_SNAPS = [1, 5, 10, 50, 100];
const ROTATION_SNAPS = [5, 10, 15, 45, 90];
const CAMERA_SPEEDS = [1, 2, 3, 4, 5, 6, 7, 8];

export function ViewportToolbar() {
  const { play } = useEditor();
  const playing = useStore(play) !== null;
  // The view's controls belong to the editor; while playing, the game has the view (reference 05).
  return playing ? null : <ViewControls />;
}

function ViewControls() {
  const settings = useEditorSettings();
  const { dispatch, camera } = useEditor();
  // Only these two: the toolbar shouldn't re-render on every camera move.
  const projection = useStoreSelector(camera, (snapshot) => snapshot.projection);
  const projectionMenu = useProjectionMenu(projection);
  const axis = useStoreSelector(camera, (snapshot) => axisViewOf(snapshot));
  const set = (key: SettingKey, value: unknown) => {
    const request: CommandRequest = {
      type: "editor.setSetting",
      payload: { key, value },
      origin: "viewport",
    };
    void dispatch(request);
  };
  const viewModeLabel = VIEW_MODES.find((mode) => mode.value === settings.viewMode)?.label ?? "Lit";

  return (
    <>
      <div className="kn-vp-toolbar kn-vp-toolbar--left" role="toolbar" aria-label="View">
        <Dropdown
          ariaLabel="Projection"
          label={projectionMenu.label}
          showChevron={false}
          items={projectionMenu.items}
        />
        <Dropdown
          ariaLabel="View direction"
          label={axis ? VIEW_LABELS[axis] : "Free"}
          showChevron={false}
          items={AXIS_VIEWS.map((view) => ({
            label: VIEW_LABELS[view],
            checked: axis === view,
            onSelect: () => {
              void dispatch({ type: "camera.setView", payload: { view }, origin: "viewport" });
            },
          }))}
        />
        <Dropdown
          ariaLabel="View mode"
          label={viewModeLabel}
          showChevron={false}
          items={VIEW_MODES.map((mode) => ({
            label: mode.label,
            checked: settings.viewMode === mode.value,
            onSelect: () => {
              set("viewMode", mode.value);
            },
          }))}
        />
        <Dropdown
          ariaLabel="Show flags"
          label="Show"
          kind="toggle"
          items={[
            ...SHOW_FLAGS.map((flag) => ({
              label: flag.label,
              checked: settings[flag.key],
              onSelect: () => {
                set(flag.key, !settings[flag.key]);
              },
            })),
            // Unreal's Game View (G): the level without any editor overlay.
            {
              label: "Game View",
              checked: settings.gameView,
              onSelect: () => {
                set("gameView", !settings.gameView);
              },
            },
          ]}
        />
      </div>

      <div
        className="kn-vp-toolbar kn-vp-toolbar--right"
        role="toolbar"
        aria-label="Snapping and camera"
      >
        <Dropdown
          className="kn-dropdown--end"
          ariaLabel="Grid snap"
          showChevron={false}
          label={
            <>
              <GridIcon size={12} className="kn-vp-toolbar__icon" /> {settings.snapTranslate}
            </>
          }
          items={GRID_SNAPS.map((value) => ({
            label: `${value} cm`,
            checked: settings.snapTranslate === value,
            onSelect: () => {
              set("snapTranslate", value);
            },
          }))}
        />
        <Dropdown
          className="kn-dropdown--end"
          ariaLabel="Rotation snap"
          showChevron={false}
          label={
            <>
              <RotateIcon size={12} className="kn-vp-toolbar__icon" /> {settings.snapRotate}°
            </>
          }
          items={ROTATION_SNAPS.map((value) => ({
            label: `${value}°`,
            checked: settings.snapRotate === value,
            onSelect: () => {
              set("snapRotate", value);
            },
          }))}
        />
        <button
          type="button"
          className="kn-vp-toolbar__item kn-vp-toolbar__snap"
          aria-pressed={settings.snapEnabled}
          onClick={() => {
            set("snapEnabled", !settings.snapEnabled);
          }}
        >
          <MagnetIcon size={12} /> Snap
        </button>
        <Dropdown
          className="kn-dropdown--end"
          ariaLabel="Camera speed"
          showChevron={false}
          // Unreal's camera speed control: an icon and the number, named in its tooltip.
          label={
            <>
              <CameraSpeedIcon size={14} />
              {settings.cameraSpeed}
            </>
          }
          items={CAMERA_SPEEDS.map((value) => ({
            label: String(value),
            section: "Camera speed",
            checked: settings.cameraSpeed === value,
            onSelect: () => {
              set("cameraSpeed", value);
            },
          }))}
        />
      </div>
    </>
  );
}

/**
 * The projection menu: Perspective or Orthographic, then the level's cameras
 * (Unreal's viewport menu lists them the same way). Choosing a camera looks
 * through it and pilots it; choosing a projection stops piloting. While
 * piloting, the menu's button names the camera.
 */
function useProjectionMenu(projection: Projection) {
  const { dispatch, pilot } = useEditor();
  const piloting = useStore(pilot);
  const world = useWorldSnapshot();
  const pilotRequest = (entity: string | null): CommandRequest => ({
    type: "camera.pilot",
    payload: { entity },
    origin: "viewport",
  });

  const projections = PROJECTIONS.map((kind) => ({
    label: PROJECTION_LABELS[kind],
    checked: piloting === null && projection === kind,
    onSelect: () => {
      if (piloting !== null) void dispatch(pilotRequest(null));
      void dispatch({
        type: "camera.setProjection",
        payload: { projection: kind },
        origin: "viewport",
      });
    },
  }));
  const cameras = [...world.entities.values()]
    .filter((entity) => entity.components.has("Camera"))
    .map((entity) => ({
      label: entity.name,
      section: "Cameras",
      checked: piloting === entity.id,
      onSelect: () => {
        void dispatch(pilotRequest(entity.id));
      },
    }));

  const pilotedName = piloting === null ? null : world.entities.get(piloting)?.name;
  return {
    label: pilotedName ?? PROJECTION_LABELS[projection],
    items: [...projections, ...cameras],
  };
}
